/**
 * Supabase Invoices API Service
 *
 * Handles all invoice operations.
 */

import { supabase } from './client';
import type { Invoice, InvoiceInsert, InvoiceUpdate, Json } from './database.types';

// Invoice status type
export type InvoiceStatus = 'pending' | 'paid' | 'unpaid' | 'overdue' | 'canceled';

// Invoice item type
export interface InvoiceItem {
  id: string;
  currency: string;
  description: string;
  quantity: number;
  unitAmount: number;
  totalAmount: number;
}

// Invoice note type
export interface InvoiceNote {
  id: string;
  title: string;
  content: string;
}

// Invoice discount type
export interface InvoiceDiscount {
  enabled: boolean;
  type: 'percentage' | 'fixed';
  amount: number;
  name: string;
}

// Frontend Invoice type (camelCase)
export interface InvoiceData {
  id: string;
  createdAt: string;
  updatedAt: string;
  leadId: string | null;
  invoiceNumber: string;
  issueDate: string;
  dueDate: string;
  status: InvoiceStatus;
  // Customer Info
  customerName: string;
  customerCompany: string | null;
  customerEmail: string | null;
  customerAddress: string | null;
  customerTaxId: string | null;
  // Line Items
  items: InvoiceItem[];
  notes: InvoiceNote[];
  discount: InvoiceDiscount | null;
  // Amounts
  subtotalAmount: number;
  discountAmount: number;
  taxRate: number;
  taxAmount: number;
  totalAmount: number;
  userId: string | null;
}

// Map database invoice to frontend format
const mapInvoiceToData = (invoice: Invoice): InvoiceData => ({
  id: invoice.id,
  createdAt: invoice.created_at,
  updatedAt: invoice.updated_at,
  leadId: invoice.lead_id,
  invoiceNumber: invoice.invoice_number,
  issueDate: invoice.issue_date,
  dueDate: invoice.due_date,
  status: invoice.status,
  customerName: invoice.customer_name,
  customerCompany: invoice.customer_company,
  customerEmail: invoice.customer_email,
  customerAddress: invoice.customer_address,
  customerTaxId: invoice.customer_tax_id,
  items: (invoice.items as unknown as InvoiceItem[]) || [],
  notes: (invoice.notes as unknown as InvoiceNote[]) || [],
  discount: invoice.discount as InvoiceDiscount | null,
  subtotalAmount: invoice.subtotal_amount,
  discountAmount: invoice.discount_amount,
  taxRate: invoice.tax_rate,
  taxAmount: invoice.tax_amount,
  totalAmount: invoice.total_amount,
  userId: invoice.user_id,
});

// Map frontend invoice to database format
const mapDataToInvoice = (data: Partial<InvoiceData>): Partial<InvoiceInsert> => {
  const mapped: Partial<InvoiceInsert> = {};

  if (data.leadId !== undefined) mapped.lead_id = data.leadId;
  if (data.invoiceNumber !== undefined) mapped.invoice_number = data.invoiceNumber;
  if (data.issueDate !== undefined) mapped.issue_date = data.issueDate;
  if (data.dueDate !== undefined) mapped.due_date = data.dueDate;
  if (data.status !== undefined) mapped.status = data.status;
  if (data.customerName !== undefined) mapped.customer_name = data.customerName;
  if (data.customerCompany !== undefined) mapped.customer_company = data.customerCompany;
  if (data.customerEmail !== undefined) mapped.customer_email = data.customerEmail;
  if (data.customerAddress !== undefined) mapped.customer_address = data.customerAddress;
  if (data.customerTaxId !== undefined) mapped.customer_tax_id = data.customerTaxId;
  if (data.items !== undefined) mapped.items = data.items as unknown as Json;
  if (data.notes !== undefined) mapped.notes = data.notes as unknown as Json;
  if (data.discount !== undefined) mapped.discount = data.discount as unknown as Json;
  if (data.subtotalAmount !== undefined) mapped.subtotal_amount = data.subtotalAmount;
  if (data.discountAmount !== undefined) mapped.discount_amount = data.discountAmount;
  if (data.taxRate !== undefined) mapped.tax_rate = data.taxRate;
  if (data.taxAmount !== undefined) mapped.tax_amount = data.taxAmount;
  if (data.totalAmount !== undefined) mapped.total_amount = data.totalAmount;
  if (data.userId !== undefined) mapped.user_id = data.userId;

  return mapped;
};

export interface GetInvoicesParams {
  leadId?: string;
  status?: InvoiceStatus;
  startDate?: string;
  endDate?: string;
  query?: string;
  page?: number;
  pageSize?: number;
}

export interface GetInvoicesResponse {
  data: InvoiceData[];
  count: number;
}

class InvoicesApi {
  /**
   * Get all invoices with optional filters
   */
  async getAll(params: GetInvoicesParams = {}): Promise<GetInvoicesResponse> {
    const pageSize = params.pageSize || 25;
    const page = params.page || 1;
    const offset = (page - 1) * pageSize;

    let query = supabase
      .from('invoices')
      .select('*', { count: 'exact' });

    // Apply filters
    if (params.leadId) {
      query = query.eq('lead_id', params.leadId);
    }

    if (params.status) {
      query = query.eq('status', params.status);
    }

    if (params.startDate) {
      query = query.gte('issue_date', params.startDate);
    }

    if (params.endDate) {
      query = query.lte('issue_date', params.endDate);
    }

    if (params.query) {
      query = query.or(`invoice_number.ilike.%${params.query}%,customer_name.ilike.%${params.query}%`);
    }

    // Apply sorting (newest first)
    query = query.order('created_at', { ascending: false });

    // Apply pagination
    query = query.range(offset, offset + pageSize - 1);

    const { data, error, count } = await query;

    if (error) {
      // console.error('Failed to fetch invoices:', error);
      throw error;
    }

    return {
      data: (data || []).map(mapInvoiceToData),
      count: count || 0,
    };
  }

  /**
   * Get invoices for a specific lead
   */
  async getByLead(leadId: string): Promise<InvoiceData[]> {
    const { data, error } = await supabase
      .from('invoices')
      .select('*')
      .eq('lead_id', leadId)
      .order('created_at', { ascending: false });

    if (error) {
      // console.error('Failed to fetch invoices by lead:', error);
      throw error;
    }

    return (data || []).map(mapInvoiceToData);
  }

  /**
   * Get a single invoice by ID
   */
  async getById(id: string): Promise<InvoiceData | null> {
    const { data, error } = await supabase
      .from('invoices')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null;
      }
      // console.error('Failed to fetch invoice:', error);
      throw error;
    }

    return data ? mapInvoiceToData(data) : null;
  }

  /**
   * Get invoice by invoice number
   */
  async getByInvoiceNumber(invoiceNumber: string): Promise<InvoiceData | null> {
    const { data, error } = await supabase
      .from('invoices')
      .select('*')
      .eq('invoice_number', invoiceNumber)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null;
      }
      // console.error('Failed to fetch invoice by number:', error);
      throw error;
    }

    return data ? mapInvoiceToData(data) : null;
  }

  /**
   * Create a new invoice
   */
  async create(invoiceData: Omit<InvoiceData, 'id' | 'createdAt' | 'updatedAt'>): Promise<InvoiceData> {
    // console.log('invoicesApi.create called with:', {
    //   invoiceNumber: invoiceData.invoiceNumber,
    //   leadId: invoiceData.leadId,
    //   customerName: invoiceData.customerName,
    // });

    const dbData = mapDataToInvoice(invoiceData) as InvoiceInsert;

    const { data, error } = await supabase
      .from('invoices')
      .insert(dbData as any)
      .select()
      .single();

    if (error) {
      // console.error('Failed to create invoice:', error);
      throw new Error(`Invoice creation failed: ${error.message}`);
    }

    // console.log('Invoice created successfully:', (data as Invoice).id);
    return mapInvoiceToData(data as Invoice);
  }

  /**
   * Update an existing invoice
   */
  async update(id: string, invoiceData: Partial<InvoiceData>): Promise<InvoiceData> {
    const dbData = mapDataToInvoice(invoiceData) as InvoiceUpdate;

    const { data, error } = await (supabase
      .from('invoices')
      .update(dbData as any)
      .eq('id', id)
      .select()
      .single() as any);

    if (error) {
      // console.error('Failed to update invoice:', error);
      throw error;
    }

    return mapInvoiceToData(data as Invoice);
  }

  /**
   * Delete an invoice
   */
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('invoices')
      .delete()
      .eq('id', id);

    if (error) {
      // console.error('Failed to delete invoice:', error);
      throw error;
    }
  }

  /**
   * Update invoice status
   */
  async updateStatus(id: string, status: InvoiceStatus): Promise<InvoiceData> {
    return this.update(id, { status });
  }

  /**
   * Mark invoice as paid
   */
  async markAsPaid(id: string): Promise<InvoiceData> {
    return this.updateStatus(id, 'paid');
  }

  /**
   * Generate next invoice number
   */
  async generateInvoiceNumber(): Promise<string> {
    const year = new Date().getFullYear();
    const prefix = `INV-${year}-`;

    try {
      const { data, error } = await supabase
        .from('invoices')
        .select('invoice_number')
        .like('invoice_number', `${prefix}%`)
        .order('invoice_number', { ascending: false })
        .limit(1);

      if (error) {
        // console.error('Error fetching latest invoice number:', error);
        return `${prefix}0001`;
      }

      let nextNumber = 1;
      if (data && data.length > 0) {
        const lastInvoiceNumber = (data[0] as { invoice_number: string }).invoice_number;
        const lastNumber = parseInt(lastInvoiceNumber.split('-').pop() || '0', 10);
        nextNumber = lastNumber + 1;
      }

      return `${prefix}${String(nextNumber).padStart(4, '0')}`;
    } catch (error) {
      // console.error('Error generating invoice number:', error);
      return `${prefix}0001`;
    }
  }

  /**
   * Get overdue invoices
   */
  async getOverdue(): Promise<InvoiceData[]> {
    const today = new Date().toISOString().split('T')[0];

    const { data, error } = await supabase
      .from('invoices')
      .select('*')
      .in('status', ['pending', 'unpaid'])
      .lt('due_date', today)
      .order('due_date', { ascending: true });

    if (error) {
      // console.error('Failed to fetch overdue invoices:', error);
      throw error;
    }

    return (data as Invoice[] || []).map(mapInvoiceToData);
  }

  /**
   * Get invoice summary statistics
   */
  async getStats(): Promise<{
    total: number;
    pending: number;
    paid: number;
    overdue: number;
    totalAmount: number;
    paidAmount: number;
    pendingAmount: number;
  }> {
    const { data, error } = await supabase
      .from('invoices')
      .select('status, total_amount');

    if (error) {
      // console.error('Failed to fetch invoice stats:', error);
      throw error;
    }

    const invoices = data as { status: InvoiceStatus; total_amount: number }[] || [];
    const stats = {
      total: invoices.length,
      pending: 0,
      paid: 0,
      overdue: 0,
      totalAmount: 0,
      paidAmount: 0,
      pendingAmount: 0,
    };

    invoices.forEach((invoice) => {
      stats.totalAmount += invoice.total_amount || 0;

      if (invoice.status === 'paid') {
        stats.paid++;
        stats.paidAmount += invoice.total_amount || 0;
      } else if (invoice.status === 'overdue') {
        stats.overdue++;
        stats.pendingAmount += invoice.total_amount || 0;
      } else {
        stats.pending++;
        stats.pendingAmount += invoice.total_amount || 0;
      }
    });

    return stats;
  }
}

export const invoicesApi = new InvoicesApi();

// Invoice status labels
export const invoiceStatusLabels: Record<InvoiceStatus, string> = {
  pending: 'Pending',
  paid: 'Paid',
  unpaid: 'Unpaid',
  overdue: 'Overdue',
  canceled: 'Canceled',
};

// Invoice status colors
export const invoiceStatusColors: Record<InvoiceStatus, string> = {
  pending: '#F59E0B',
  paid: '#10B981',
  unpaid: '#6B7280',
  overdue: '#EF4444',
  canceled: '#EF4444',
};
